import { useEffect, useState } from "react";
import {
  FaChevronLeft, FaChevronRight, FaExclamationTriangle, FaCheckSquare, FaRegSquare, FaCheck,
} from "react-icons/fa";
import Modal from "../Modal";

export default function ModalEncuesta({
  token,
  encuesta,
  miembro,
  onClose
}) {
  const [secciones, setSecciones] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [tabActiva, setTabActiva] = useState(0);
  const [animando, setAnimando] = useState(false);
  const [direccion, setDireccion] = useState("right");
  const [error, setError] = useState("");

  // =========================
  // 📌 EFFECT (CORREGIDO)
  // =========================
  useEffect(() => {
    if (!encuesta) return;

    const fetchEncuesta = async () => {
      const res = await fetch(
        `/api/encuesta/${encuesta.id}`,
        { credentials: "include", headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setSecciones(data.secciones || []);
    };

    const fetchRespuestas = async () => {
      try {
        const res = await fetch(
          `/api/encuesta/respuestas/${encuesta.id}/${miembro.id_miembro}`,
          { credentials: "include", headers: { Authorization: `Bearer ${token}` } }
        );

        const data = await res.json();
        const formateadas = {};

        data.forEach(r => {
          if (r.respuesta_texto !== null) {
            formateadas[r.pregunta_id] = r.respuesta_texto;
          } else if (r.respuesta_boolean !== null) {
            formateadas[r.pregunta_id] = r.respuesta_boolean;
          } else {
            formateadas[r.pregunta_id] = r.opciones || [];
          }
        });

        setRespuestas(formateadas);
      } catch {
        console.log("Sin respuestas previas");
      }
    };

    fetchEncuesta();
    fetchRespuestas();

  }, [encuesta, token, miembro.id_miembro]);

  // =========================
  // 📌 HANDLERS
  // =========================
  const handleChange = (pregunta, valor) => {
    setRespuestas(prev => ({
      ...prev,
      [pregunta.id]: valor
    }));
  };

  const handleCheckbox = (pregunta, opcionId) => {
    const actuales = respuestas[pregunta.id] || [];

    if (actuales.includes(opcionId)) {
      handleChange(
        pregunta,
        actuales.filter(o => o !== opcionId)
      );
    } else {
      handleChange(pregunta, [...actuales, opcionId]);
    }
  };

  // =========================
  // 📌 VALIDACIÓN
  // =========================
  const validar = () => {
    for (const sec of secciones) {
      for (const p of sec.preguntas) {
        const r = respuestas[p.id];

        if (p.tipo === "texto" && (!r || r.trim() === "")) {
          setError("Todas las preguntas deben ser respondidas");
          return false;
        }

        if (p.tipo === "checkbox" && (!r || r.length === 0)) {
          setError("Todas las preguntas deben ser respondidas");
          return false;
        }
      }
    }

    setError("");
    return true;
  };

  // =========================
  // 📌 NAVEGACIÓN
  // =========================
  const cambiarTab = (nuevoIndex) => {
    if (nuevoIndex < 0 || nuevoIndex >= secciones.length) return;

    setDireccion(nuevoIndex > tabActiva ? "right" : "left");
    setAnimando(true);

    setTimeout(() => {
      setTabActiva(nuevoIndex);
      setAnimando(false);
    }, 200);
  };

  // =========================
  // 📌 GUARDAR
  // =========================
  const guardar = async () => {
    if (!validar()) return;

    const payload = [];

    secciones.forEach(sec => {
      sec.preguntas.forEach(p => {
        let valor = respuestas[p.id];

        if (p.tipo === "si_no" && valor === undefined) {
          valor = false;
        }

        payload.push({
          pregunta_id: p.id,
          tipo: p.tipo,
          valor: valor || null,
          opciones: valor || []
        });
      });
    });

    await fetch("/api/encuesta/responder", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        encuesta_id: encuesta.id,
        id_miembro: miembro.id_miembro,
        respuestas: payload
      })
    });

    onClose();
  };

  // =========================
  // 🎨 UI
  // =========================
  return (
    <Modal open={true} onClose={onClose} title={`Censo - ${miembro.nombre}`} maxWidth="max-w-4xl">

        {/* HEADER */}
        <div className="text-xs font-medium text-slate-500 mb-4">
          Paso {tabActiva + 1} de {secciones.length}
        </div>

        {/* STEPPER */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {secciones.map((sec, i) => (
              <div key={sec.id} className="flex-1 flex items-center">
                <div className="flex flex-col items-center w-full">
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold transition
                      ${
                        i === tabActiva
                          ? "bg-indigo-600 text-white"
                          : i < tabActiva
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-100 text-slate-400"
                      }`}
                  >
                    {i < tabActiva ? <FaCheck className="text-xs" /> : i + 1}
                  </div>
                  <span className="text-[11px] mt-1.5 text-center text-slate-600 font-medium">
                    {sec.nombre}
                  </span>
                </div>

                {i !== secciones.length - 1 && (
                  <div className="flex-1 h-0.5 bg-slate-200 mx-2 mb-5">
                    <div
                      className={`h-0.5 transition-all ${
                        i < tabActiva ? "bg-emerald-500" : "bg-slate-200"
                      }`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CONTENIDO */}
        <div
          className={`transition-all duration-200 ${
            animando
              ? direccion === "right"
                ? "opacity-0 translate-x-10"
                : "opacity-0 -translate-x-10"
              : "opacity-100 translate-x-0"
          }`}
        >
          {secciones[tabActiva] && (
            <div className="space-y-3">
              {secciones[tabActiva].preguntas.map(p => (
                <div
                  key={p.id}
                  className="bg-slate-50 border border-slate-200 rounded-xl p-4"
                >
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    {p.texto}
                  </label>

                  {/* TEXTO */}
                  {p.tipo === "texto" && (
                    <input
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                      value={respuestas[p.id] || ""}
                      onChange={(e) =>
                        handleChange(p, e.target.value)
                      }
                    />
                  )}

                  {/* SI / NO */}
                  {p.tipo === "si_no" && (
                    <div className="flex gap-2">
                      {[
                        { label: "Sí", value: true },
                        { label: "No", value: false }
                      ].map(op => (
                        <button
                          key={op.label}
                          onClick={() => handleChange(p, op.value)}
                          className={`px-4 py-1.5 text-sm font-medium rounded-lg border transition
                            ${
                              respuestas[p.id] === op.value
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                            }`}
                        >
                          {op.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* CHECKBOX */}
                  {p.tipo === "checkbox" && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {p.opciones.map(op => {
                        const seleccionado = (respuestas[p.id] || []).includes(op.id);
                        return (
                          <label
                            key={op.id}
                            className={`flex justify-between items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition
                              ${
                                seleccionado
                                  ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-100"
                              }`}
                          >
                            <span>{op.texto}</span>

                            {seleccionado ? (
                              <FaCheckSquare className="text-indigo-600 shrink-0" />
                            ) : (
                              <FaRegSquare className="text-slate-300 shrink-0" />
                            )}

                            <input
                              type="checkbox"
                              className="hidden"
                              checked={seleccionado}
                              onChange={() =>
                                handleCheckbox(p, op.id)
                              }
                            />
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ERROR */}
        {error && (
          <div className="flex items-center gap-2 bg-rose-50 text-rose-600 border border-rose-200 text-sm px-3 py-2.5 rounded-lg mt-4">
            <FaExclamationTriangle className="text-xs shrink-0" /> {error}
          </div>
        )}

        {/* BOTONES */}
        <div className="flex justify-between items-center mt-5 pt-4 border-t border-slate-100">
          <button
            onClick={() => cambiarTab(tabActiva - 1)}
            disabled={tabActiva === 0}
            className="flex items-center gap-1.5 px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition"
          >
            <FaChevronLeft className="text-[10px]" /> Anterior
          </button>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>

            <button
              onClick={guardar}
              className="flex items-center gap-1.5 px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition"
            >
              <FaCheck className="text-[10px]" /> Guardar
            </button>
          </div>

          <button
            onClick={() => cambiarTab(tabActiva + 1)}
            disabled={tabActiva === secciones.length - 1}
            className="flex items-center gap-1.5 px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium disabled:opacity-40 transition"
          >
            Siguiente <FaChevronRight className="text-[10px]" />
          </button>
        </div>
    </Modal>
  );
}